<template>
  <div class="pb-10">
    <!-- En-tête -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-3">
        <BaseBackButton @click="$router.push({ name: 'suppliers' })" />
        <div>
          <h1 class="text-lg font-semibold text-gray-900">Fiche fournisseur</h1>
          <p class="text-xs text-gray-400">Détails et historique des commandes</p>
        </div>
      </div>
      <div v-if="supplier.id" class="flex gap-2">
        <button v-permission="'supplier-invoices.create'" @click="createInvoice"
          class="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors">
          <i class="fas fa-file-invoice text-[10px]"></i>
          Créer une facture
        </button>
        <button v-permission="'suppliers.edit'" @click="editSupplier"
          class="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors">
          <i class="fas fa-edit text-[10px]"></i>
          Modifier
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex flex-col items-center justify-center py-20">
      <span class="w-8 h-8 border-3 border-gray-200 border-t-primary rounded-full animate-spin mb-3"></span>
      <p class="text-xs text-gray-400">Chargement des informations...</p>
    </div>

    <!-- Not found -->
    <div v-else-if="!supplier.id" class="py-20 text-center">
      <div class="flex flex-col items-center">
        <div class="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
          <i class="fas fa-circle-info text-red-300 text-xl"></i>
        </div>
        <h3 class="text-sm font-semibold text-gray-700 mb-1">Fournisseur introuvable</h3>
        <p class="text-xs text-gray-400 mb-4">Le fournisseur que vous recherchez n'existe pas ou a été supprimé</p>
        <button @click="$router.push({ name: 'suppliers' })"
          class="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors">
          <i class="fas fa-arrow-left text-[10px]"></i>
          Retour à la liste
        </button>
      </div>
    </div>

    <!-- Content -->
    <div v-else class="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
      <!-- Sidebar - Profile Card -->
      <div>
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <!-- Profile header -->
          <div class="bg-primary px-6 py-6 flex flex-col items-center gap-3">
            <div class="w-16 h-16 rounded-full bg-white flex items-center justify-center">
              <span class="text-xl font-extrabold text-primary">{{ getInitials(supplier.name) }}</span>
            </div>
            <span :class="[
              'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold',
              supplier.active ? 'bg-emerald-400/20 text-emerald-100' : 'bg-white/20 text-white/80'
            ]">
              <span :class="[
                'w-1.5 h-1.5 rounded-full',
                supplier.active ? 'bg-emerald-300' : 'bg-white/60'
              ]"></span>
              {{ supplier.active ? 'Actif' : 'Inactif' }}
            </span>
          </div>

          <!-- Name & date -->
          <div class="px-6 py-5 text-center border-b border-gray-100">
            <h2 class="text-lg font-bold text-gray-900 mb-1">{{ supplier.name }}</h2>
            <div class="flex items-center justify-center gap-1.5 text-[11px] text-gray-400">
              <i class="fas fa-calendar text-[9px]"></i>
              <span>Créé le {{ formatDate(supplier.created_at) }}</span>
            </div>
          </div>

          <!-- Contact info -->
          <div class="px-6 py-5 border-b border-gray-100">
            <h4 class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-4">Informations de contact</h4>

            <div v-if="supplier.contact_person" class="flex items-start gap-3 mb-3">
              <div class="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center flex-shrink-0">
                <i class="fas fa-user text-sky-400 text-[10px]"></i>
              </div>
              <div>
                <p class="text-[10px] text-gray-400 mb-0.5">Personne contact</p>
                <p class="text-xs font-medium text-gray-800">{{ supplier.contact_person }}</p>
              </div>
            </div>

            <div v-if="supplier.phone" class="flex items-start gap-3 mb-3">
              <div class="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center flex-shrink-0">
                <i class="fas fa-phone text-sky-400 text-[10px]"></i>
              </div>
              <div>
                <p class="text-[10px] text-gray-400 mb-0.5">Téléphone</p>
                <a :href="'tel:' + supplier.phone" class="text-xs font-medium text-primary hover:underline">{{ supplier.phone }}</a>
              </div>
            </div>

            <div v-if="supplier.email" class="flex items-start gap-3 mb-3">
              <div class="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center flex-shrink-0">
                <i class="fas fa-envelope text-sky-400 text-[10px]"></i>
              </div>
              <div>
                <p class="text-[10px] text-gray-400 mb-0.5">Email</p>
                <a :href="'mailto:' + supplier.email" class="text-xs font-medium text-primary hover:underline break-all">{{ supplier.email }}</a>
              </div>
            </div>

            <div v-if="supplier.full_address" class="flex items-start gap-3">
              <div class="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center flex-shrink-0">
                <i class="fas fa-map-marker-alt text-sky-400 text-[10px]"></i>
              </div>
              <div>
                <p class="text-[10px] text-gray-400 mb-0.5">Adresse</p>
                <p class="text-xs font-medium text-gray-800">{{ supplier.full_address }}</p>
              </div>
            </div>

            <div v-if="!supplier.phone && !supplier.email && !supplier.full_address && !supplier.contact_person"
              class="text-center py-6">
              <i class="fas fa-circle-info text-gray-200 text-lg mb-2"></i>
              <p class="text-[11px] text-gray-400">Aucune information de contact</p>
            </div>
          </div>

          <!-- Notes -->
          <div v-if="supplier.notes" class="px-6 py-5">
            <h4 class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Notes</h4>
            <p class="text-xs text-gray-600 leading-relaxed bg-gray-50 rounded-lg p-3">{{ supplier.notes }}</p>
          </div>
        </div>
      </div>

      <!-- Main content -->
      <div>
        <!-- Stats -->
        <div class="grid grid-cols-3 gap-4 mb-5">
          <div class="bg-white border border-gray-200 rounded-xl px-4 py-4 text-center hover:shadow-sm transition-shadow">
            <p class="text-xl font-extrabold text-gray-900">{{ supplierStats.ordersCount }}</p>
            <p class="text-[11px] text-gray-400 font-medium mt-1">Commandes</p>
          </div>
          <div class="bg-white border border-gray-200 rounded-xl px-4 py-4 text-center hover:shadow-sm transition-shadow">
            <p class="text-xl font-extrabold text-gray-900">{{ formatCurrency(supplierStats.totalPurchased) }}</p>
            <p class="text-[11px] text-gray-400 font-medium mt-1">Total acheté</p>
          </div>
          <div :class="[
            'border rounded-xl px-4 py-4 text-center hover:shadow-sm transition-shadow',
            supplierStats.unreceivedCount > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'
          ]">
            <p :class="['text-xl font-extrabold', supplierStats.unreceivedCount > 0 ? 'text-amber-700' : 'text-gray-900']">
              {{ supplierStats.unreceivedCount }}
            </p>
            <p class="text-[11px] text-gray-400 font-medium mt-1">En attente</p>
          </div>
        </div>

        <!-- Orders card -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <!-- Card header -->
          <div class="px-5 py-4 border-b border-gray-100 bg-gray-50/80">
            <h3 class="text-sm font-semibold text-gray-900">Historique des commandes</h3>
            <p class="text-[11px] text-gray-400 mt-0.5">{{ supplierStats.ordersCount }} commande{{ supplierStats.ordersCount > 1 ? 's' : '' }}</p>
          </div>

          <!-- Orders loading -->
          <div v-if="ordersLoading" class="flex flex-col items-center justify-center py-14">
            <span class="w-7 h-7 border-3 border-gray-200 border-t-primary rounded-full animate-spin mb-3"></span>
            <p class="text-xs text-gray-400">Chargement des commandes...</p>
          </div>

          <!-- Orders empty -->
          <div v-else-if="orders.length === 0" class="py-14 text-center">
            <div class="flex flex-col items-center">
              <div class="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-3">
                <i class="fas fa-file text-gray-300 text-lg"></i>
              </div>
              <h4 class="text-sm font-semibold text-gray-700 mb-1">Aucune commande</h4>
              <p class="text-xs text-gray-400">Ce fournisseur n'a pas encore de commandes enregistrées</p>
            </div>
          </div>

          <!-- Orders list -->
          <div v-else>
            <div v-for="order in orders" :key="order.id"
              class="flex items-center gap-4 px-5 py-4 border-b border-gray-50 last:border-b-0 hover:bg-gray-50/50 cursor-pointer transition-colors group"
              @click="viewOrder(order)">
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-3 mb-2">
                  <span class="text-sm font-semibold text-gray-900">{{ order.reference }}</span>
                  <span :class="[
                    'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold',
                    getStatusClasses(order.status)
                  ]">
                    {{ getOrderStatusLabel(order.status) }}
                  </span>
                </div>
                <div class="flex items-center gap-4 flex-wrap">
                  <span class="flex items-center gap-1.5 text-[11px] text-gray-400">
                    <i class="fas fa-calendar text-[8px]"></i>
                    {{ formatDate(order.order_date || order.created_at) }}
                  </span>
                  <span class="flex items-center gap-1.5 text-[11px] text-gray-400">
                    <i class="fas fa-tag text-[8px]"></i>
                    {{ order.items_count }} article{{ order.items_count > 1 ? 's' : '' }}
                  </span>
                  <span class="flex items-center gap-1.5 text-[11px] text-gray-400">
                    <i class="fas fa-user text-[8px]"></i>
                    {{ order.user?.name || '-' }}
                  </span>
                  <span :class="[
                    'inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold',
                    order.has_invoice ? 'bg-emerald-50 text-emerald-700' : 'bg-orange-50 text-orange-600'
                  ]">
                    {{ order.has_invoice ? 'Facturée' : 'Non facturée' }}
                  </span>
                </div>
              </div>
              <div class="text-right flex-shrink-0">
                <p class="text-sm font-bold text-primary">{{ formatCurrency(order.total_amount || 0) }}</p>
              </div>
              <i class="fas fa-chevron-right text-[9px] text-gray-300 group-hover:text-primary transition-colors flex-shrink-0"></i>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="shouldShowOrdersPagination">
            <PaginationComponent
              :currentPage="ordersPagination.page"
              :perPage="Number(ordersPagination.per_page)"
              :total="ordersPagination.total"
              :to="ordersPagination.to || Math.min(ordersPagination.page * Number(ordersPagination.per_page), ordersPagination.total)"
              :showPageNumbers="true"
              :maxVisiblePages="5"
              @page-change="goToOrdersPage"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import supplierService from '@/services/supplierService'
import purchaseOrderService from '@/services/purchaseOrderService'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import BaseBackButton from '@/components/base/BaseBackButton.vue'

export default defineComponent({
  name: 'SupplierDetailView',
  components: {
    PaginationComponent,
    BaseBackButton
  },
  setup() {
    const route = useRoute()
    const router = useRouter()

    const supplier = ref({})
    const loading = ref(true)
    const orders = ref([])
    const ordersLoading = ref(false)
    const ordersPagination = ref({
      page: 1,
      per_page: 3,
      total: 0,
      from: 0,
      to: 0
    })

    const supplierStats = ref({
      ordersCount: 0,
      totalPurchased: 0,
      unreceivedCount: 0
    })

    const loadSupplier = async () => {
      const id = route.params.id
      if (!id) {
        router.push({ name: 'suppliers' })
        return
      }
      try {
        loading.value = true
        ordersPagination.value.page = 1
        const res = await supplierService.getSupplier(id)
        supplier.value = res.data.data || res.data
        await loadSupplierOrders()
      } catch (e) {
        console.error('Erreur chargement fournisseur:', e)
        supplier.value = {}
      } finally {
        loading.value = false
      }
    }

    const loadSupplierOrders = async () => {
      try {
        ordersLoading.value = true
        const res = await purchaseOrderService.getPurchaseOrders({
          supplier_id: supplier.value.id,
          per_page: ordersPagination.value.per_page,
          page: ordersPagination.value.page,
          sort_by: 'created_at',
          sort_direction: 'desc'
        })
        const data = res.data
        if (Array.isArray(data)) {
          orders.value = data
          ordersPagination.value.total = data.length
          ordersPagination.value.from = data.length ? 1 : 0
          ordersPagination.value.to = data.length
        } else {
          orders.value = data.data || data.items || []
          ordersPagination.value.total = Number(data.total || (data.meta && data.meta.total) || orders.value.length) || 0
          ordersPagination.value.per_page = Number(data.per_page || (data.meta && data.meta.per_page) || ordersPagination.value.per_page) || ordersPagination.value.per_page
          ordersPagination.value.page = Number(data.current_page || (data.meta && data.meta.current_page) || ordersPagination.value.page) || ordersPagination.value.page
          ordersPagination.value.from = Number(data.from || (data.meta && data.meta.from) || ((ordersPagination.value.page - 1) * ordersPagination.value.per_page + 1)) || 0
          ordersPagination.value.to = Number(data.to || (data.meta && data.meta.to) || Math.min(ordersPagination.value.page * ordersPagination.value.per_page, ordersPagination.value.total)) || 0
        }

        supplierStats.value.ordersCount = Number(Array.isArray(data) ? orders.value.length : (ordersPagination.value.total || orders.value.length) || 0)
        supplierStats.value.totalPurchased = orders.value.reduce((acc, o) => acc + Number(o.total_amount || o.total_incl_tax || o.total || 0), 0)
        supplierStats.value.unreceivedCount = orders.value.filter(o => (o.status === 'pending' || o.status === 'ordered' || o.status === 'inactive')).length
      } catch (e) {
        console.error('Erreur chargement commandes fournisseur:', e)
        orders.value = []
        ordersPagination.value.total = 0
        ordersPagination.value.from = 0
        ordersPagination.value.to = 0
      } finally {
        ordersLoading.value = false
      }
    }

    const goToOrdersPage = (page) => {
      ordersPagination.value.page = page
      loadSupplierOrders()
    }

    const viewOrder = (order) => {
      router.push({ name: 'purchase-order-details', params: { id: order.id } })
    }

    const editSupplier = () => {
      console.log('TODO: Naviguer vers édition fournisseur', supplier.value.id)
    }

    const createInvoice = () => {
      router.push({ name: 'supplier-invoices-new', params: { supplierId: supplier.value.id } })
    }

    const getOrderStatusLabel = (status) => {
      const map = {
        active: 'Active',
        inactive: 'Inactive',
        pending: 'En attente',
        ordered: 'Commandée',
        received: 'Réceptionnée',
        cancelled: 'Annulée'
      }
      return map[status] || status
    }

    const getStatusClasses = (status) => {
      const map = {
        active: 'bg-emerald-50 text-emerald-700',
        inactive: 'bg-gray-100 text-gray-500',
        pending: 'bg-amber-50 text-amber-700',
        ordered: 'bg-blue-50 text-blue-700',
        received: 'bg-emerald-50 text-emerald-700',
        cancelled: 'bg-red-50 text-red-600'
      }
      return map[status] || 'bg-gray-100 text-gray-500'
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount || 0) + ' FCFA'
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      })
    }

    const getInitials = (name) => {
      if (!name) return '?'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    }

    const shouldShowOrdersPagination = computed(() => {
      return ordersPagination.value.total >= ordersPagination.value.per_page && ordersPagination.value.per_page > 0
    })

    onMounted(() => {
      loadSupplier()
    })

    return {
      supplier,
      loading,
      orders,
      ordersLoading,
      supplierStats,
      ordersPagination,
      shouldShowOrdersPagination,
      goToOrdersPage,
      viewOrder,
      editSupplier,
      createInvoice,
      getOrderStatusLabel,
      getStatusClasses,
      formatCurrency,
      formatDate,
      getInitials
    }
  }
})
</script>
